import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  ICommitteeMemberInput,
  IExecutiveMemberInput,
} from "../../exports/interfaces";
// import { dynamicSuccess } from "../../hooks/useToast";
import Select from "react-select";
import {
  customSelectStyles,
  SelectOption,
} from "../../exports/customSelectStyles";
import { locationData } from "../../exports/location";
import { Upload } from "lucide-react";
import { useOption } from "../../context/OptionContext";
import {
  convertNepaliToEnglishDate,
  formatDateBySetting,
  renderExistingFilePreview,
  renderFilePreview,
  validateFileSize,
  validatePassportSizePhoto,
} from "../../exports/functions";
import CommiteeTable from "./CommiteeTable";
import ExecutiveTable from "./ExecutiveTable";
import {
  getCommitteeMemberErrors,
  getExecutiveMemberErrors,
} from "./registerValidation";
import NepaliCalendar from "../NepaliCalendar";
import useMobile from "../../hooks/useMobile";
import { useSettings } from "../../context/SettingsContext";

const SelectWithError = Select as unknown as React.ComponentType<any>;

const Committee = ({
  committeeactiveTab,
  committeeCooperative,
  setCommitteeCooperative,
  executiveCooperative,
  setExecutiveCooperative,
  errorsCommittee,
  errorsExecutive,
  committeeTableCooperative,
  setCommitteeTableCooperative,
  editCommitteeIndex,
  setEditCommitteeIndex,
  executiveTableCooperative,
  setExecutiveTableCooperative,
  editExecutiveIndex,
  setEditExecutiveIndex,
  setErrors,
}: {
  committeeactiveTab: string;
  committeeCooperative: ICommitteeMemberInput;
  setCommitteeCooperative: Dispatch<SetStateAction<ICommitteeMemberInput>>;
  executiveCooperative: IExecutiveMemberInput;
  setExecutiveCooperative: Dispatch<SetStateAction<IExecutiveMemberInput>>;
  errorsCommittee: any;
  errorsExecutive: any;
  committeeTableCooperative: ICommitteeMemberInput[];
  setCommitteeTableCooperative: Dispatch<
    SetStateAction<ICommitteeMemberInput[]>
  >;
  editCommitteeIndex: number | null;
  setEditCommitteeIndex: Dispatch<SetStateAction<number | null>>;
  executiveTableCooperative: IExecutiveMemberInput[];
  setExecutiveTableCooperative: Dispatch<
    SetStateAction<IExecutiveMemberInput[]>
  >;
  editExecutiveIndex: number | null;
  setEditExecutiveIndex: Dispatch<SetStateAction<number | null>>;
  setErrors: any;
}) => {
  const { categories } = useOption();
  const { generalSettings } = useSettings();
  const isMobile = useMobile();
  const tenureStartDropdownRef = useRef<HTMLDivElement>(null);
  const tenureEndDropdownRef = useRef<HTMLDivElement>(null);
  const [showTenureStartCalendar, setShowTenureStartCalendar] = useState(false);
  const [showTenureEndCalendar, setShowTenureEndCalendar] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tenureStartDropdownRef.current &&
        !tenureStartDropdownRef.current.contains(event.target as Node)
      ) {
        setShowTenureStartCalendar(false);
      }
      if (
        tenureEndDropdownRef.current &&
        !tenureEndDropdownRef.current.contains(event.target as Node)
      ) {
        setShowTenureEndCalendar(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    inputFile: string,
    field?: string,
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!validateFileSize(file)) {
        e.target.value = "";
        return;
      }
      if (field === "photo") {
        const isValidPhoto = await validatePassportSizePhoto(file);

        if (!isValidPhoto) {
          e.target.value = "";
          return;
        }
      }
      if (inputFile === "CommitteeMember") {
        setCommitteeCooperative((prev) => ({
          ...prev,
          ...(field === "bodFile"
            ? {
                bodFile: file,
                bodFileName: file.name,
                bodDocumentUrl: "",
              }
            : field === "citizenshipFront"
              ? {
                  citizenshipFront: file,
                  citizenshipFrontName: file.name,
                  citizenshipFrontUrl: "",
                }
              : field === "citizenshipBack"
                ? {
                    citizenshipBack: file,
                    citizenshipBackName: file.name,
                    citizenshipBackUrl: "",
                  }
                : {
                    fileName: file.name,
                    file: file,
                    documentUrl: "",
                  }),
        }));
      } else {
        setExecutiveCooperative((prev) => ({
          ...prev,
          ...(field === "citizenshipFront"
            ? {
                citizenshipFront: file,
                citizenshipFrontName: file.name,
                citizenshipFrontUrl: "",
              }
            : field === "citizenshipBack"
              ? {
                  citizenshipBack: file,
                  citizenshipBackName: file.name,
                  citizenshipBackUrl: "",
                }
              : { fileName: file.name, file, documentUrl: "" }),
        }));
      }
    }
  };

  const renderSelectValue = (
    options: SelectOption[],
    value: string | number,
  ) => {
    return options.find((opt) => opt.value === value) || null;
  };

  const committeeTypeOptions =
    categories
      ?.find((item) => item.name === "Committee Type")
      ?.dropdowns?.filter((item) => item.status === "Active")
      ?.sort((a, b) => a.order - b.order)
      ?.map((item) => ({
        value: item.label,
        label: item.label,
      })) || [];

  const committeePositionOptions =
    categories
      ?.find((item) => item.name === "Committee Position")
      ?.dropdowns?.filter((item) => item.status === "Active")
      ?.sort((a, b) => a.order - b.order)
      ?.map((item) => ({
        value: item.label,
        label: item.label,
      })) || [];

  const positionStatusOptions =
    categories
      ?.find((item) => item.name === "Position Status")
      ?.dropdowns?.filter((item) => item.status === "Active")
      ?.sort((a, b) => a.order - b.order)
      ?.map((item) => ({
        value: item.label,
        label: item.label,
      })) || [];

  const educationOptions =
    categories
      ?.find((item) => item.name === "Education Level")
      ?.dropdowns?.filter((item) => item.status === "Active")
      ?.sort((a, b) => a.order - b.order)
      ?.map((item) => ({
        value: item.label,
        label: item.label,
      })) || [];

  const executivePositionOptions =
    categories
      ?.find((item) => item.name === "Executive Position")
      ?.dropdowns?.filter((item) => item.status === "Active")
      ?.sort((a, b) => a.order - b.order)
      ?.map((item) => ({
        value: item.label,
        label: item.label,
      })) || [];

  const executiveStatusOptions =
    categories
      ?.find((item) => item.name === "Position Status")
      ?.dropdowns?.filter((item) => item.status === "Active")
      ?.sort((a, b) => a.order - b.order)
      ?.map((item) => ({
        value: item.label,
        label: item.label,
      })) || [];

  const genderOptions =
    categories
      ?.find((item) => item.name === "Gender")
      ?.dropdowns?.filter((item) => item.status === "Active")
      ?.sort((a, b) => a.order - b.order)
      ?.map((item) => ({
        value: item.label,
        label: item.label,
      })) || [];

  const maritalStatusOptions = [
    { value: "Single", label: "Single" },
    { value: "Married", label: "Married" },
  ];

  const issueddDistrictOptions = locationData.flatMap((province: any) => {
    const districts = Array.isArray(province.districts)
      ? province.districts
      : Object.values(province.districts || {});

    return districts.map((district: any) => ({
      label: district.name,
      value: district.name,
    }));
  });

  // const issueddDistrictOptions =
  //   categories
  //     ?.find((item) => item.name === "District")
  //     ?.dropdowns?.filter((item) => item.status === "Active")
  //     ?.sort((a, b) => a.order - b.order)
  //     ?.map((item) => ({
  //       value: item.label,
  //       label: item.label,
  //     })) || [];

  const provinces = Array.isArray(locationData)
    ? locationData
    : Object.values(locationData || {});

  const provinceOptions = provinces.map((province: any) => ({
    label: province.name,
    value: province.name,
  }));

  // const provinceOptions =
  //   categories
  //     ?.find((item) => item.name === "Province")
  //     ?.dropdowns?.filter((item) => item.status === "Active")
  //     ?.sort((a, b) => a.order - b.order)
  //     ?.map((item) => ({
  //       value: item.label,
  //       label: item.label,
  //     })) || [];

  const selectedProvince: any = provinces.find(
    (province: any) => province.name === committeeCooperative.province,
  );

  const districtsRaw = selectedProvince?.districts ?? {};

  const districts = Array.isArray(districtsRaw)
    ? districtsRaw
    : Object.values(districtsRaw);

  const districtOptions = districts.map((district: any) => ({
    label: district.name,
    value: district.name,
  }));

  const selectedExecutiveProvince: any = provinces.find(
    (province: any) => province.name === executiveCooperative.province,
  );

  const districtsExecutiveRaw = selectedExecutiveProvince?.districts ?? {};

  const executiveDistricts = Array.isArray(districtsExecutiveRaw)
    ? districtsExecutiveRaw
    : Object.values(districtsExecutiveRaw);

  const districtExecutiveOptions = executiveDistricts.map((district: any) => ({
    label: district.name,
    value: district.name,
  }));

  // const districtOptions =
  //   categories
  //     ?.find((item) => item.name === "District")
  //     ?.dropdowns?.filter((item) => item.status === "Active")
  //     ?.sort((a, b) => a.order - b.order)
  //     ?.map((item) => ({
  //       value: item.label,
  //       label: item.label,
  //     })) || [];

  const selectedDistrict: any = districts.find(
    (d: any) => d.name === committeeCooperative.district,
  );

  const municipalitiesRaw = selectedDistrict?.municipalities ?? {};

  const municipalities = Array.isArray(municipalitiesRaw)
    ? municipalitiesRaw
    : Object.values(municipalitiesRaw);

  const municipalityOptions = municipalities.map((district: any) => ({
    label: district.name,
    value: district.name,
  }));

  const selectedExecutiveDistrict: any = executiveDistricts.find(
    (district: any) => district.name === executiveCooperative.district,
  );

  const executiveMunicipalitiesRaw =
    selectedExecutiveDistrict?.municipalities ?? {};

  const executiveMunicipalities = Array.isArray(executiveMunicipalitiesRaw)
    ? executiveMunicipalitiesRaw
    : Object.values(executiveMunicipalitiesRaw);

  const executiveMunicipalityOptions = executiveMunicipalities.map(
    (municipality: any) => ({
      label: municipality.name,
      value: municipality.name,
    }),
  );

  // const municipalityOptions =
  //   categories
  //     ?.find((item) => item.name === "Municipality (Local Level)")
  //     ?.dropdowns?.filter((item) => item.status === "Active")
  //     ?.sort((a, b) => a.order - b.order)
  //     ?.map((item) => ({
  //       value: item.label,
  //       label: item.label,
  //     })) || [];
  const selectedMunicipality: any = municipalities.find(
    (m: any) => m.name === committeeCooperative.municipality,
  );
  const wardsRaw = selectedMunicipality?.wards ?? [];

  const wardOptions = (
    Array.isArray(wardsRaw) ? wardsRaw : Object.values(wardsRaw)
  ).map((ward: any) => ({
    label: ward.toString(),
    value: ward.toString(),
  }));

  const selectedExecutiveMunicipality: any = executiveMunicipalities.find(
    (municipality: any) =>
      municipality.name === executiveCooperative.municipality,
  );

  const executiveWardsRaw = selectedExecutiveMunicipality?.wards ?? [];

  const executiveWardOptions = (
    Array.isArray(executiveWardsRaw)
      ? executiveWardsRaw
      : Object.values(executiveWardsRaw)
  ).map((ward: any) => ({
    label: ward.toString(),
    value: ward.toString(),
  }));

  // const wardOptions =
  //   categories
  //     ?.find((item) => item.name === "Ward")
  //     ?.dropdowns?.filter((item) => item.status === "Active")
  //     ?.sort((a, b) => a.order - b.order)
  //     ?.map((item) => ({
  //       value: item.label,
  //       label: item.label,
  //     })) || [];

  const handleCommitteeSelectChange = (
    name: keyof ICommitteeMemberInput,
    selected: SelectOption | null,
  ) => {
    const value = selected?.value || "";

    setCommitteeCooperative((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "maritalStatus" && value === "Single"
        ? { husbandName: "" }
        : {}),
    }));
  };

  const handleExecutiveSelectChange = (
    name: keyof IExecutiveMemberInput,
    selected: SelectOption | null,
  ) => {
    const value = selected?.value || "";

    setExecutiveCooperative((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "maritalStatus" && value === "Single"
        ? { husbandName: "" }
        : {}),
    }));
  };
  // const [committeeData, setCommitteeData] = useState<any[]>([]);

  // const [executiveData, setExecutiveData] = useState<any[]>([]);

  const [committeeErrors, setCommitteeErrors] = useState<
    Record<string, string>
  >({});

  const handleSaveCommittee = () => {
    const validationErrors = getCommitteeMemberErrors(committeeCooperative);

    let hasErrors = false;

    if (Object.keys(validationErrors).length > 0) {
      setCommitteeErrors({ ...validationErrors });
      hasErrors = true;
    }

    if (!committeeCooperative.fiscalYear?.trim()) {
      setErrors((prev: any) => ({
        ...prev,
        committeeCooperative: {
          fiscalYear: "Fiscal Year is required.",
        },
      }));
      hasErrors = true;
    } else {
      setErrors({});
    }

    if (hasErrors) {
      return;
    }

    // // Clear previous errors
    // setCommitteeErrors((prev: any) => ({
    //   ...prev,
    //   committeeCooperative: {},
    // }));
    setErrors({});
    setCommitteeErrors({});
    if (!setCommitteeTableCooperative) return;

    if (editCommitteeIndex !== null) {
      setCommitteeTableCooperative((prev) =>
        prev.map((item, index) =>
          index === editCommitteeIndex ? { ...committeeCooperative } : item,
        ),
      );

      setEditCommitteeIndex(null);
    } else {
      setCommitteeTableCooperative((prev) => [
        ...prev,
        { ...committeeCooperative },
      ]);
    }

    setCommitteeCooperative({
      fiscalYear: "",
      committeeType: "",
      position: "",
      positionStatus: "",
      grandmotherName: "",
      fatherName: "",
      maritalStatus: "",
      husbandName: "",
      fullName: "",
      gender: "",
      mobile: "",
      email: "",
      address: "",

      citizenship: "",
      pan: "",
      nid: "",
      issuedDistrict: "",
      tenureStart: "",
      tenureEnd: "",

      province: "",
      district: "",
      municipality: "",
      ward: "",
      street: "",

      education: "",
      file: null,
      fileName: "",
      bodFile: null,
      bodFileName: "",
      citizenshipFront: null,
      citizenshipFrontName: "",
      citizenshipBack: null,
      citizenshipBackName: "",
    });
    setExecutiveCooperative((prev: any) => ({
      ...prev,
      fiscalYear: "",
    }));
  };

  const [executiveErrors, setExecutiveErrors] = useState<
    Record<string, string>
  >({});

  const handleSaveExecutive = () => {
    const validationErrors = getExecutiveMemberErrors(executiveCooperative);

    let hasErrors = false;

    if (Object.keys(validationErrors).length > 0) {
      setExecutiveErrors({ ...validationErrors });
      hasErrors = true;
    }

    if (!committeeCooperative.fiscalYear?.trim()) {
      setErrors((prev: any) => ({
        ...prev,
        committeeCooperative: {
          fiscalYear: "Fiscal Year is required.",
        },
      }));
      hasErrors = true;
    } else {
      setErrors({});
    }

    if (hasErrors) {
      return;
    }

    setErrors({});
    setExecutiveErrors({});
    if (!setExecutiveTableCooperative) return;
    if (editExecutiveIndex !== null) {
      setExecutiveTableCooperative((prev) =>
        prev.map((item, index) =>
          index === editExecutiveIndex ? { ...executiveCooperative } : item,
        ),
      );

      setEditExecutiveIndex(null);
    } else {
      setExecutiveTableCooperative((prev) => [
        ...prev,
        { ...executiveCooperative },
      ]);
    }

    setExecutiveCooperative({
      fiscalYear: "",
      position: "",
      positionStatus: "",
      grandmotherName: "",
      fatherName: "",
      maritalStatus: "",
      husbandName: "",
      fullName: "",
      gender: "",

      email: "",
      address: "",
      mobile: "",
      pan: "",
      nid: "",
      citizenship: "",
      issuedDistrict: "",

      province: "",
      district: "",
      municipality: "",
      ward: "",
      street: "",

      experience: null,
      currentPositionDuration: null,
      education: "",
      file: null,
      fileName: "",
      citizenshipFront: null,
      citizenshipFrontName: "",
      citizenshipBack: null,
      citizenshipBackName: "",
    });
    setCommitteeCooperative((prev: any) => ({
      ...prev,
      fiscalYear: "",
    }));
  };

  const handleCommitteeChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    let updatedValue = value;

    if (name === "pan") {
      updatedValue = value.replace(/\D/g, "").slice(0, 9);
    }

    if (name === "nid") {
      updatedValue = value.replace(/\D/g, "").slice(0, 10);
    }

    if (name === "mobile") {
      updatedValue = value.replace(/\D/g, "").slice(0, 10);
    }

    if (name === "citizenship") {
      updatedValue = value.replace(/\D/g, "").slice(0, 14);
    }

    setCommitteeCooperative((prev: any) => ({
      ...prev,
      [name]: updatedValue,
    }));
  };

  const handleExecutiveChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    let updatedValue = value;

    if (name === "pan") {
      updatedValue = value.replace(/\D/g, "").slice(0, 9);
    }

    if (name === "nid") {
      updatedValue = value.replace(/\D/g, "").slice(0, 10);
    }

    if (name === "citizenship") {
      updatedValue = value.replace(/\D/g, "").slice(0, 14);
    }

    if (name === "mobile") {
      updatedValue = value.replace(/\D/g, "").slice(0, 10);
    }

    setExecutiveCooperative((prev: any) => ({
      ...prev,
      [name]: updatedValue,
    }));
  };

  return (
    <>
      <div className={`dashboard-card`} style={{ borderTopLeftRadius: 0 }}>
        <div className="row g-3">
          <div className="col-12">
            {committeeactiveTab === "committees" && (
              <>
                <div className="dashboard-card">
                  <div className="row gx-5 gy-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Committee Type <span className="primary-color">*</span>
                      </label>
                      <SelectWithError
                        styles={customSelectStyles}
                        options={committeeTypeOptions}
                        placeholder="Select Committee Type"
                        isError={Boolean(committeeErrors.committeeType)}
                        value={renderSelectValue(
                          committeeTypeOptions,
                          committeeCooperative.committeeType,
                        )}
                        onChange={(selected: SelectOption | null) =>
                          handleCommitteeSelectChange("committeeType", selected)
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {committeeErrors.committeeType && (
                        <p className="error-message">
                          {committeeErrors.committeeType}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Position <span className="primary-color">*</span>
                      </label>
                      <SelectWithError
                        styles={customSelectStyles}
                        options={committeePositionOptions}
                        isError={Boolean(committeeErrors.position)}
                        placeholder="Select Position"
                        value={renderSelectValue(
                          committeePositionOptions,
                          committeeCooperative.position,
                        )}
                        onChange={(selected: SelectOption | null) =>
                          handleCommitteeSelectChange("position", selected)
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {committeeErrors.position && (
                        <p className="error-message">
                          {committeeErrors.position}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Position Status <span className="primary-color">*</span>
                      </label>
                      <SelectWithError
                        styles={customSelectStyles}
                        options={positionStatusOptions}
                        placeholder="Select Position Status"
                        isError={Boolean(committeeErrors.positionStatus)}
                        value={renderSelectValue(
                          positionStatusOptions,
                          committeeCooperative.positionStatus,
                        )}
                        onChange={(selected: SelectOption | null) =>
                          handleCommitteeSelectChange(
                            "positionStatus",
                            selected,
                          )
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {committeeErrors.positionStatus && (
                        <p className="error-message">
                          {committeeErrors.positionStatus}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div
                  className="dashboard-card mt-3"
                  style={{ overflow: "unset" }}
                >
                  <div className="row gx-5 gy-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Full Name <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        className={`form-control ${
                          committeeErrors.fullName ? "input-error" : ""
                        }`}
                        placeholder="Enter Full Name"
                        onChange={handleCommitteeChange}
                        value={committeeCooperative.fullName}
                      />
                      {committeeErrors.fullName && (
                        <p className="error-message">
                          {committeeErrors.fullName}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Gender <span className="primary-color">*</span>
                      </label>
                      <SelectWithError
                        styles={customSelectStyles}
                        options={genderOptions}
                        placeholder="Select Gender"
                        isError={Boolean(committeeErrors.gender)}
                        value={renderSelectValue(
                          genderOptions,
                          committeeCooperative.gender,
                        )}
                        onChange={(selected: SelectOption | null) =>
                          handleCommitteeSelectChange("gender", selected)
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {committeeErrors.gender && (
                        <p className="error-message">
                          {committeeErrors.gender}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Mobile Number <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="\d*"
                        maxLength={10}
                        name="mobile"
                        className={`form-control ${
                          committeeErrors.mobile ? "input-error" : ""
                        }`}
                        placeholder="Enter Mobile Number"
                        onChange={handleCommitteeChange}
                        value={committeeCooperative.mobile}
                      />
                      {committeeErrors.mobile && (
                        <p className="error-message">
                          {committeeErrors.mobile}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Email Address <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        name="email"
                        className={`form-control ${
                          committeeErrors.email ? "input-error" : ""
                        }`}
                        onChange={handleCommitteeChange}
                        value={committeeCooperative.email}
                        placeholder="Enter Email Address"
                      />
                      {committeeErrors.email && (
                        <p className="error-message">{committeeErrors.email}</p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Address <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        name="address"
                        className={`form-control ${
                          committeeErrors.address ? "input-error" : ""
                        }`}
                        placeholder="Enter Address"
                        onChange={handleCommitteeChange}
                        value={committeeCooperative.address}
                      />
                      {committeeErrors.address && (
                        <p className="error-message">
                          {committeeErrors.address}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Grandfather / Grandmother Name{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        name="grandmotherName"
                        className={`form-control ${
                          committeeErrors.grandmotherName ? "input-error" : ""
                        }`}
                        placeholder="Enter Grandfather / Grandmother Name"
                        onChange={handleCommitteeChange}
                        value={committeeCooperative.grandmotherName}
                      />
                      {committeeErrors.grandmotherName && (
                        <p className="error-message">
                          {committeeErrors.grandmotherName}
                        </p>
                      )}
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">
                        Father / Mother Name{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        name="fatherName"
                        className={`form-control ${
                          committeeErrors.fatherName ? "input-error" : ""
                        }`}
                        placeholder="Father / Mother Name"
                        onChange={handleCommitteeChange}
                        value={committeeCooperative.fatherName}
                      />
                      {committeeErrors.fatherName && (
                        <p className="error-message">
                          {committeeErrors.fatherName}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Marital Status <span className="primary-color">*</span>
                      </label>
                      <SelectWithError
                        styles={customSelectStyles}
                        options={maritalStatusOptions}
                        placeholder="Select Marital Status"
                        isError={Boolean(committeeErrors.maritalStatus)}
                        value={renderSelectValue(
                          maritalStatusOptions,
                          committeeCooperative.maritalStatus,
                        )}
                        onChange={(selected: SelectOption | null) =>
                          handleCommitteeSelectChange("maritalStatus", selected)
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {committeeErrors.maritalStatus && (
                        <p className="error-message">
                          {committeeErrors.maritalStatus}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Husband / Wife Name{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        name="husbandName"
                        className={`form-control ${
                          committeeErrors.husbandName ? "input-error" : ""
                        }`}
                        placeholder="Husband / Wife Name"
                        onChange={handleCommitteeChange}
                        value={committeeCooperative.husbandName}
                        disabled={
                          committeeCooperative.maritalStatus !== "Married"
                        }
                      />
                      {committeeCooperative.maritalStatus === "Married" &&
                        committeeErrors.husbandName && (
                          <p className="error-message">
                            {committeeErrors.husbandName}
                          </p>
                        )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Citizenship Number{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="\d*"
                        maxLength={14}
                        name="citizenship"
                        className={`form-control ${
                          committeeErrors.citizenship ? "input-error" : ""
                        }`}
                        placeholder="Enter Citizenship Number"
                        onChange={handleCommitteeChange}
                        value={committeeCooperative.citizenship}
                      />
                      {committeeErrors.citizenship && (
                        <p className="error-message">
                          {committeeErrors.citizenship}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        PAN Number
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="\d*"
                        maxLength={9}
                        name="pan"
                        className={`form-control ${
                          committeeErrors.pan ? "input-error" : ""
                        }`}
                        placeholder="Enter PAN Number"
                        onChange={handleCommitteeChange}
                        value={committeeCooperative.pan}
                      />
                      {committeeErrors.pan && (
                        <p className="error-message">{committeeErrors.pan}</p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        NID Number <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="\d*"
                        maxLength={10}
                        name="nid"
                        className={`form-control ${
                          committeeErrors.nid ? "input-error" : ""
                        }`}
                        placeholder="Enter NID Number"
                        onChange={handleCommitteeChange}
                        value={committeeCooperative.nid}
                      />
                      {committeeErrors.nid && (
                        <p className="error-message">{committeeErrors.nid}</p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Issued District <span className="primary-color">*</span>
                      </label>
                      <SelectWithError
                        styles={customSelectStyles}
                        options={issueddDistrictOptions}
                        placeholder="Select Issued District"
                        isError={Boolean(committeeErrors.issuedDistrict)}
                        value={renderSelectValue(
                          issueddDistrictOptions,
                          committeeCooperative.issuedDistrict,
                        )}
                        onChange={(selected: SelectOption | null) =>
                          handleCommitteeSelectChange(
                            "issuedDistrict",
                            selected,
                          )
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {committeeErrors.issuedDistrict && (
                        <p className="error-message">
                          {committeeErrors.issuedDistrict}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4" data-field="tenureStart">
                      <label className="form-label">
                        Tenure Start Date{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <div
                        className="dropdown position-relative"
                        ref={tenureStartDropdownRef}
                      >
                        <button
                          type="button"
                          className={`form-control text-start nepali-date-picker ${
                            committeeErrors.tenureStart ? "input-error" : ""
                          }`}
                          onClick={() =>
                            setShowTenureStartCalendar(!showTenureStartCalendar)
                          }
                        >
                          <div className="d-flex justify-content-between align-items-center w-100">
                            <span className="text-truncate">
                              {formatDateBySetting(
                                committeeCooperative.tenureStart,
                                generalSettings?.dateFormat,
                              ) ||
                                generalSettings?.dateFormat ||
                                "mm/dd/yyyy"}
                            </span>

                            <i className="fa-regular fa-calendar"></i>
                          </div>
                        </button>

                        {showTenureStartCalendar && (
                          <div
                            className="dropdown-menu show p-0"
                            style={{
                              width: isMobile ? "249px" : "560px",
                              height: isMobile ? "407px" : "380px",
                              border: "1px solid #ddd",
                              zIndex: 1050,
                            }}
                          >
                            <NepaliCalendar
                              onDateSelect={(nepaliDate, englishDate) => {
                                const convertedEnglishDate =
                                  convertNepaliToEnglishDate(
                                    nepaliDate,
                                    englishDate,
                                  );
                                setCommitteeCooperative((prev: any) => ({
                                  ...prev,
                                  tenureStart: convertedEnglishDate,
                                }));
                                setShowTenureStartCalendar(false);
                              }}
                            />
                          </div>
                        )}
                      </div>
                      {committeeErrors.tenureStart && (
                        <p className="error-message">
                          {committeeErrors.tenureStart}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4" data-field="tenureEnd">
                      <label className="form-label">
                        Tenure End Date <span className="primary-color">*</span>
                      </label>
                      <div
                        className="dropdown position-relative"
                        ref={tenureEndDropdownRef}
                      >
                        <button
                          type="button"
                          className={`form-control text-start nepali-date-picker ${
                            committeeErrors.tenureEnd ? "input-error" : ""
                          }`}
                          onClick={() =>
                            setShowTenureEndCalendar(!showTenureEndCalendar)
                          }
                        >
                          <div className="d-flex justify-content-between align-items-center w-100">
                            <span className="text-truncate">
                              {formatDateBySetting(
                                committeeCooperative.tenureEnd,
                                generalSettings?.dateFormat,
                              ) ||
                                generalSettings?.dateFormat ||
                                "mm/dd/yyyy"}
                            </span>

                            <i className="fa-regular fa-calendar"></i>
                          </div>
                        </button>

                        {showTenureEndCalendar && (
                          <div
                            className="dropdown-menu dropdown-menu-end show p-0"
                            style={{
                              width: isMobile ? "249px" : "560px",
                              height: isMobile ? "407px" : "380px",
                              border: "1px solid #ddd",
                              zIndex: 1050,
                              right: 0,
                              left: "auto",
                            }}
                          >
                            <NepaliCalendar
                              onDateSelect={(nepaliDate, englishDate) => {
                                const convertedEnglishDate =
                                  convertNepaliToEnglishDate(
                                    nepaliDate,
                                    englishDate,
                                  );
                                setCommitteeCooperative((prev: any) => ({
                                  ...prev,
                                  tenureEnd: convertedEnglishDate,
                                }));
                                setShowTenureEndCalendar(false);
                              }}
                            />
                          </div>
                        )}
                      </div>
                      {committeeErrors.tenureEnd && (
                        <p className="error-message">
                          {committeeErrors.tenureEnd}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="dashboard-card mt-3">
                  <div className="row gx-5 gy-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Province <span className="primary-color">*</span>
                      </label>
                      <SelectWithError
                        styles={customSelectStyles}
                        options={provinceOptions}
                        placeholder="Select Province"
                        isError={Boolean(committeeErrors.province)}
                        value={renderSelectValue(
                          provinceOptions,
                          committeeCooperative.province,
                        )}
                        onChange={(selected: SelectOption | null) =>
                          handleCommitteeSelectChange("province", selected)
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {committeeErrors.province && (
                        <p className="error-message">
                          {committeeErrors.province}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        District <span className="primary-color">*</span>
                      </label>
                      <SelectWithError
                        styles={customSelectStyles}
                        options={districtOptions}
                        placeholder="Select District"
                        isError={Boolean(committeeErrors.district)}
                        value={renderSelectValue(
                          districtOptions,
                          committeeCooperative.district,
                        )}
                        onChange={(selected: SelectOption | null) =>
                          handleCommitteeSelectChange("district", selected)
                        }
                        noOptionsMessage={() =>
                          !committeeCooperative.province
                            ? "Select province"
                            : "No options"
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {committeeErrors.district && (
                        <p className="error-message">
                          {committeeErrors.district}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Municipality (Local Level){" "}
                        <span className="primary-color">*</span>
                      </label>
                      <SelectWithError
                        styles={customSelectStyles}
                        options={municipalityOptions}
                        placeholder="Select Municipality"
                        isError={Boolean(committeeErrors.municipality)}
                        value={renderSelectValue(
                          municipalityOptions,
                          committeeCooperative.municipality,
                        )}
                        onChange={(selected: SelectOption | null) =>
                          handleCommitteeSelectChange("municipality", selected)
                        }
                        noOptionsMessage={() =>
                          !committeeCooperative.district
                            ? "Select district"
                            : "No options"
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {committeeErrors.municipality && (
                        <p className="error-message">
                          {committeeErrors.municipality}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Ward <span className="primary-color">*</span>
                      </label>
                      <SelectWithError
                        styles={customSelectStyles}
                        options={wardOptions}
                        placeholder="Select Ward"
                        isError={Boolean(committeeErrors.ward)}
                        value={
                          wardOptions.find(
                            (option) =>
                              option.value === committeeCooperative.ward,
                          ) || null
                        }
                        onChange={(selected: SelectOption | null) =>
                          handleCommitteeSelectChange("ward", selected)
                        }
                        noOptionsMessage={() =>
                          !committeeCooperative.municipality
                            ? "Select municipality"
                            : "No options"
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {committeeErrors.ward && (
                        <p className="error-message">{committeeErrors.ward}</p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Street \ Tole <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        name="street"
                        className={`form-control ${
                          committeeErrors.street ? "input-error" : ""
                        }`}
                        placeholder="Enter Street \ Tole"
                        onChange={handleCommitteeChange}
                        value={committeeCooperative.street}
                      />
                      {committeeErrors.street && (
                        <p className="error-message">
                          {committeeErrors.street}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Highest Level of Education{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <SelectWithError
                        styles={customSelectStyles}
                        options={educationOptions}
                        placeholder="Select Highest Level of Education"
                        isError={Boolean(committeeErrors.education)}
                        value={renderSelectValue(
                          educationOptions,
                          committeeCooperative.education || "",
                        )}
                        onChange={(selected: SelectOption | null) =>
                          handleCommitteeSelectChange("education", selected)
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {committeeErrors.education && (
                        <p className="error-message">
                          {committeeErrors.education}
                        </p>
                      )}
                    </div>
                    <div className="col-12">
                      <label className="form-label">
                        Upload Committee Member Photo (JPEG, PNG){" "}
                        <span className="primary-color">*</span>
                        <small className="fw-bold" style={{ fontSize: "12px" }}>
                          {" "}
                          (Passport-size photo required (35 × 45 mm))
                        </small>
                      </label>

                      <div
                        className={`upload-box p-4 ${
                          committeeErrors.file ? "input-error" : ""
                        } ${
                          !committeeCooperative.file &&
                          !committeeCooperative.documentUrl &&
                          "text-center"
                        }`}
                      >
                        {committeeCooperative.file ? (
                          renderFilePreview(
                            committeeCooperative.file,
                            committeeCooperative.fileName,
                            (e: any) =>
                              handleFileChange(e, "CommitteeMember", "photo"),
                          )
                        ) : committeeCooperative.documentUrl ? (
                          renderExistingFilePreview(
                            committeeCooperative.documentUrl,
                            (e: any) =>
                              handleFileChange(e, "CommitteeMember", "photo"),
                          )
                        ) : (
                          <>
                            <Upload />

                            <p className="mb-1">
                              Choose a file or drag and drop it here.
                            </p>

                            <small className="d-block mb-3">
                              (jpeg, png - Up to 15 mb)
                            </small>

                            <label className="btn btn-badge text-main-color">
                              Browse File
                              <input
                                hidden
                                type="file"
                                accept="image/jpeg,image/png"
                                onChange={(e) => {
                                  handleFileChange(
                                    e,
                                    "CommitteeMember",
                                    "photo",
                                  );
                                }}
                              />
                            </label>
                          </>
                        )}
                      </div>
                      {committeeErrors.file && (
                        <p className="error-message">{committeeErrors.file}</p>
                      )}
                    </div>
                    <div className="col-12">
                      <label className="form-label">
                        Upload Committees Member Citizenship Photo Front and
                        Back
                      </label>
                      <div className="row">
                        {(["citizenshipFront", "citizenshipBack"] as const).map(
                          (side) => {
                            const file = committeeCooperative[side];
                            const fileName =
                              committeeCooperative[`${side}Name`];
                            const url = committeeCooperative[`${side}Url`];
                            return (
                              <div className="col-md-6" key={side}>
                                <label className="form-label">
                                  Citizenship{" "}
                                  {side === "citizenshipFront"
                                    ? "Front"
                                    : "Back"}{" "}
                                  (JPEG, PNG){" "}
                                  <span className="primary-color">*</span>
                                </label>
                                <div
                                  className={`upload-box p-4 ${committeeErrors[side] ? "input-error" : ""} ${!file && !url ? "text-center" : ""}`}
                                >
                                  {file ? (
                                    renderFilePreview(
                                      file,
                                      fileName,
                                      (e: any) =>
                                        handleFileChange(
                                          e,
                                          "CommitteeMember",
                                          side,
                                        ),
                                    )
                                  ) : url ? (
                                    renderExistingFilePreview(url, (e: any) =>
                                      handleFileChange(
                                        e,
                                        "CommitteeMember",
                                        side,
                                      ),
                                    )
                                  ) : (
                                    <>
                                      <Upload />
                                      <p className="mb-1">
                                        Choose an image or drag and drop it
                                        here.
                                      </p>
                                      <small className="d-block mb-3">
                                        (jpeg, png - Up to 15 mb)
                                      </small>
                                      <label className="btn btn-badge text-main-color">
                                        Browse File
                                        <input
                                          hidden
                                          type="file"
                                          accept="image/jpeg,image/png"
                                          onChange={(e) =>
                                            handleFileChange(
                                              e,
                                              "CommitteeMember",
                                              side,
                                            )
                                          }
                                        />
                                      </label>
                                    </>
                                  )}
                                </div>
                                {committeeErrors[side] && (
                                  <p className="error-message">
                                    {committeeErrors[side]}
                                  </p>
                                )}
                              </div>
                            );
                          },
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {/* <div className={`dashboard-card  mt-3`}>
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="mb-3">
                        <h6>Board of Director and Audit Committee</h6>
                        <small>BOD Documents</small>
                      </div>
                      <label className="form-label">
                        Upload Document (PDF, JPEG, PNG)
                      </label>
                      <div
                        className={`upload-box p-4 ${
                          committeeErrors.bodFile ? "input-error" : ""
                        } ${
                          !committeeCooperative.bodFile &&
                          !committeeCooperative.bodDocumentUrl &&
                          "text-center"
                        }`}
                      >
                        {committeeCooperative.bodFile ? (
                          renderFilePreview(
                            committeeCooperative.bodFile,
                            committeeCooperative.bodFileName,
                            (e: any) =>
                              handleFileChange(e, "CommitteeMember", "bodFile"),
                          )
                        ) : committeeCooperative.bodDocumentUrl ? (
                          renderExistingFilePreview(
                            committeeCooperative.bodDocumentUrl,
                            (e: any) =>
                              handleFileChange(e, "CommitteeMember", "bodFile"),
                          )
                        ) : (
                          <>
                            <Upload />

                            <p className="mb-1">
                              Choose a file or drag and drop it here.
                            </p>

                            <small className="d-block mb-3">
                              (txt, docx, xlsx, pdf, jpeg, png - Up to 15 mb)
                            </small>

                            <label className="btn btn-badge text-main-color">
                              Browse File
                              <input
                                hidden
                                type="file"
                                onChange={(e) => {
                                  handleFileChange(
                                    e,
                                    "CommitteeMember",
                                    "bodFile",
                                  );
                                }}
                              />
                            </label>
                          </>
                        )}
                      </div>
                      {committeeErrors.bodFile && (
                        <p className="error-message">
                          {committeeErrors.bodFile}
                        </p>
                      )}
                    </div>
                  </div>
                </div> */}
                <div className="d-flex mt-4 justify-content-end align-items-center gap-3">
                  {errorsCommittee && (
                    <p className="error-message">{errorsCommittee}</p>
                  )}
                  <button
                    className="btn btn-badge-primary px-3"
                    onClick={handleSaveCommittee}
                  >
                    {editCommitteeIndex !== null ? "Save Member" : "Add Member"}
                  </button>
                </div>
              </>
            )}
            {committeeactiveTab === "executive" && (
              <>
                <div className="dashboard-card">
                  <div className="row gx-5 gy-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Position <span className="primary-color">*</span>
                      </label>
                      <SelectWithError
                        styles={customSelectStyles}
                        options={executivePositionOptions}
                        placeholder="Select Position"
                        isError={Boolean(executiveErrors.position)}
                        value={renderSelectValue(
                          executivePositionOptions,
                          executiveCooperative.position,
                        )}
                        onChange={(selected: SelectOption | null) =>
                          handleExecutiveSelectChange("position", selected)
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {executiveErrors.position && (
                        <p className="error-message">
                          {executiveErrors.position}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Position Status <span className="primary-color">*</span>
                      </label>
                      <SelectWithError
                        styles={customSelectStyles}
                        options={executiveStatusOptions}
                        placeholder="Select Status"
                        isError={Boolean(executiveErrors.positionStatus)}
                        value={renderSelectValue(
                          executiveStatusOptions,
                          executiveCooperative.positionStatus,
                        )}
                        onChange={(selected: SelectOption | null) =>
                          handleExecutiveSelectChange(
                            "positionStatus",
                            selected,
                          )
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {executiveErrors.positionStatus && (
                        <p className="error-message">
                          {executiveErrors.positionStatus}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="dashboard-card mt-3">
                  <div className="row gx-5 gy-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Full Name <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        className={`form-control ${
                          executiveErrors.fullName ? "input-error" : ""
                        }`}
                        placeholder="Enter Full Name"
                        onChange={handleExecutiveChange}
                        value={executiveCooperative.fullName}
                      />
                      {executiveErrors.fullName && (
                        <p className="error-message">
                          {executiveErrors.fullName}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Gender <span className="primary-color">*</span>
                      </label>
                      <SelectWithError
                        styles={customSelectStyles}
                        options={genderOptions}
                        placeholder="Select Gender"
                        isError={Boolean(executiveErrors.gender)}
                        value={renderSelectValue(
                          genderOptions,
                          executiveCooperative.gender,
                        )}
                        onChange={(selected: SelectOption | null) =>
                          handleExecutiveSelectChange("gender", selected)
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {executiveErrors.gender && (
                        <p className="error-message">
                          {executiveErrors.gender}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Mobile Number <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="\d*"
                        maxLength={10}
                        name="mobile"
                        className={`form-control ${
                          executiveErrors.mobile ? "input-error" : ""
                        }`}
                        placeholder="Enter Mobile Number"
                        onChange={handleExecutiveChange}
                        value={executiveCooperative.mobile}
                      />
                      {executiveErrors.mobile && (
                        <p className="error-message">
                          {executiveErrors.mobile}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Email Address <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        name="email"
                        className={`form-control ${
                          executiveErrors.email ? "input-error" : ""
                        }`}
                        onChange={handleExecutiveChange}
                        value={executiveCooperative.email}
                        placeholder="Enter Email Address"
                      />
                      {executiveErrors.email && (
                        <p className="error-message">{executiveErrors.email}</p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Address <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        name="address"
                        className={`form-control ${
                          executiveErrors.address ? "input-error" : ""
                        }`}
                        placeholder="Enter Address"
                        onChange={handleExecutiveChange}
                        value={executiveCooperative.address}
                      />
                      {executiveErrors.address && (
                        <p className="error-message">
                          {executiveErrors.address}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Grandfather / Grandmother Name{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        name="grandmotherName"
                        className={`form-control ${
                          executiveErrors.grandmotherName ? "input-error" : ""
                        }`}
                        placeholder="Enter Grandfather / Grandmother Name"
                        onChange={handleExecutiveChange}
                        value={executiveCooperative.grandmotherName}
                      />
                      {executiveErrors.grandmotherName && (
                        <p className="error-message">
                          {executiveErrors.grandmotherName}
                        </p>
                      )}
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">
                        Father / Mother Name{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        name="fatherName"
                        className={`form-control ${
                          executiveErrors.fatherName ? "input-error" : ""
                        }`}
                        placeholder="Enter Father / Mother Name"
                        onChange={handleExecutiveChange}
                        value={executiveCooperative.fatherName}
                      />
                      {executiveErrors.fatherName && (
                        <p className="error-message">
                          {executiveErrors.fatherName}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Marital Status <span className="primary-color">*</span>
                      </label>
                      <SelectWithError
                        styles={customSelectStyles}
                        options={maritalStatusOptions}
                        placeholder="Select Marital Status"
                        isError={Boolean(executiveErrors.maritalStatus)}
                        value={renderSelectValue(
                          maritalStatusOptions,
                          executiveCooperative.maritalStatus,
                        )}
                        onChange={(selected: SelectOption | null) =>
                          handleExecutiveSelectChange("maritalStatus", selected)
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {executiveErrors.maritalStatus && (
                        <p className="error-message">
                          {executiveErrors.maritalStatus}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Husband / Wife Name{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        name="husbandName"
                        className={`form-control ${
                          executiveErrors.husbandName ? "input-error" : ""
                        }`}
                        placeholder="Enter Husband / Wife Name"
                        onChange={handleExecutiveChange}
                        value={executiveCooperative.husbandName}
                        disabled={
                          executiveCooperative.maritalStatus !== "Married"
                        }
                      />
                      {executiveCooperative.maritalStatus === "Married" &&
                        executiveErrors.husbandName && (
                          <p className="error-message">
                            {executiveErrors.husbandName}
                          </p>
                        )}
                    </div>
                  </div>
                </div>
                <div className="dashboard-card mt-3">
                  <div className="row gx-5 gy-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Citizenship Number{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="\d*"
                        maxLength={14}
                        name="citizenship"
                        className={`form-control ${
                          executiveErrors.citizenship ? "input-error" : ""
                        }`}
                        placeholder="Enter Citizenship Number"
                        onChange={handleExecutiveChange}
                        value={executiveCooperative.citizenship}
                      />
                      {executiveErrors.citizenship && (
                        <p className="error-message">
                          {executiveErrors.citizenship}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        PAN Number
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="\d*"
                        maxLength={9}
                        name="pan"
                        className={`form-control ${
                          executiveErrors.pan ? "input-error" : ""
                        }`}
                        placeholder="Enter PAN Number"
                        onChange={handleExecutiveChange}
                        value={executiveCooperative.pan}
                      />
                      {executiveErrors.pan && (
                        <p className="error-message">{executiveErrors.pan}</p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        NID Number <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="\d*"
                        maxLength={10}
                        name="nid"
                        className={`form-control ${
                          executiveErrors.nid ? "input-error" : ""
                        }`}
                        placeholder="Enter NID Number"
                        onChange={handleExecutiveChange}
                        value={executiveCooperative.nid}
                      />
                      {executiveErrors.nid && (
                        <p className="error-message">{executiveErrors.nid}</p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Issued District <span className="primary-color">*</span>
                      </label>
                      <SelectWithError
                        styles={customSelectStyles}
                        options={issueddDistrictOptions}
                        placeholder="Select Issued District"
                        isError={Boolean(executiveErrors.issuedDistrict)}
                        value={renderSelectValue(
                          issueddDistrictOptions,
                          executiveCooperative.issuedDistrict,
                        )}
                        onChange={(selected: SelectOption | null) =>
                          handleExecutiveSelectChange(
                            "issuedDistrict",
                            selected,
                          )
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {executiveErrors.issuedDistrict && (
                        <p className="error-message">
                          {executiveErrors.issuedDistrict}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Province <span className="primary-color">*</span>
                      </label>
                      <SelectWithError
                        styles={customSelectStyles}
                        options={provinceOptions}
                        placeholder="Select Province"
                        isError={Boolean(executiveErrors.province)}
                        value={renderSelectValue(
                          provinceOptions,
                          executiveCooperative.province,
                        )}
                        onChange={(selected: SelectOption | null) =>
                          handleExecutiveSelectChange("province", selected)
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {executiveErrors.province && (
                        <p className="error-message">
                          {executiveErrors.province}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        District <span className="primary-color">*</span>
                      </label>
                      <SelectWithError
                        styles={customSelectStyles}
                        options={districtExecutiveOptions}
                        placeholder="Select District"
                        isError={Boolean(executiveErrors.district)}
                        value={renderSelectValue(
                          districtExecutiveOptions,
                          executiveCooperative.district,
                        )}
                        onChange={(selected: SelectOption | null) =>
                          handleExecutiveSelectChange("district", selected)
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {executiveErrors.district && (
                        <p className="error-message">
                          {executiveErrors.district}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Municipality (Local Level){" "}
                        <span className="primary-color">*</span>
                      </label>
                      <SelectWithError
                        styles={customSelectStyles}
                        options={executiveMunicipalityOptions}
                        placeholder="Select Municipality"
                        isError={Boolean(executiveErrors.municipality)}
                        value={renderSelectValue(
                          executiveMunicipalityOptions,
                          executiveCooperative.municipality,
                        )}
                        onChange={(selected: SelectOption | null) =>
                          handleExecutiveSelectChange("municipality", selected)
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {executiveErrors.municipality && (
                        <p className="error-message">
                          {executiveErrors.municipality}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Ward <span className="primary-color">*</span>
                      </label>
                      <SelectWithError
                        styles={customSelectStyles}
                        options={executiveWardOptions}
                        placeholder="Select Ward"
                        isError={Boolean(executiveErrors.ward)}
                        value={
                          executiveWardOptions.find(
                            (option) =>
                              option.value === executiveCooperative.ward,
                          ) || null
                        }
                        onChange={(selected: SelectOption | null) =>
                          handleExecutiveSelectChange("ward", selected)
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {executiveErrors.ward && (
                        <p className="error-message">{executiveErrors.ward}</p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Street \ Tole <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        name="street"
                        className={`form-control ${
                          executiveErrors.street ? "input-error" : ""
                        }`}
                        placeholder="Enter Street \ Tole"
                        onChange={handleExecutiveChange}
                        value={executiveCooperative.street}
                      />
                      {executiveErrors.street && (
                        <p className="error-message">
                          {executiveErrors.street}
                        </p>
                      )}
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">
                        Highest Level of Education{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <SelectWithError
                        styles={customSelectStyles}
                        options={educationOptions}
                        placeholder="Select Highest Level of Education"
                        isError={Boolean(executiveErrors.education)}
                        value={renderSelectValue(
                          educationOptions,
                          executiveCooperative.education,
                        )}
                        onChange={(selected: SelectOption | null) =>
                          handleExecutiveSelectChange("education", selected)
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {executiveErrors.education && (
                        <p className="error-message">
                          {executiveErrors.education}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Years of Experience{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (["-", "e", "+", "."].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        name="experience"
                        className={`form-control with-spin ${
                          executiveErrors.experience ? "input-error" : ""
                        }`}
                        placeholder="Enter Years of Experience"
                        onChange={handleExecutiveChange}
                        value={executiveCooperative.experience ?? ""}
                      />
                      {executiveErrors.experience && (
                        <p className="error-message">
                          {executiveErrors.experience}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Duration in Current Position (Years){" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (["-", "e", "+", "."].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        name="currentPositionDuration"
                        className={`form-control with-spin ${
                          executiveErrors.currentPositionDuration
                            ? "input-error"
                            : ""
                        }`}
                        placeholder="Enter Duration in Current Position"
                        onChange={handleExecutiveChange}
                        value={
                          executiveCooperative.currentPositionDuration ?? ""
                        }
                      />
                      {executiveErrors.currentPositionDuration && (
                        <p className="error-message">
                          {executiveErrors.currentPositionDuration}
                        </p>
                      )}
                    </div>
                    <div className="col-12">
                      <label className="form-label">
                        Upload Executive Member Photo (JPEG, PNG){" "}
                        <span className="primary-color">*</span>
                        <small className="fw-bold" style={{ fontSize: "12px" }}>
                          {" "}
                          (Passport-size photo required (35 × 45 mm))
                        </small>
                      </label>

                      <div
                        className={`upload-box p-4 ${
                          executiveErrors.file ? "input-error" : ""
                        } ${
                          !executiveCooperative.file &&
                          !executiveCooperative.documentUrl &&
                          "text-center"
                        }`}
                      >
                        {executiveCooperative.file ? (
                          renderFilePreview(
                            executiveCooperative.file,
                            executiveCooperative.fileName,
                            (e: any) => handleFileChange(e, "ExecutiveMember"),
                          )
                        ) : executiveCooperative.documentUrl ? (
                          renderExistingFilePreview(
                            executiveCooperative.documentUrl,
                            (e: any) => handleFileChange(e, "ExecutiveMember"),
                          )
                        ) : (
                          <>
                            <Upload />

                            <p className="mb-1">
                              Choose a file or drag and drop it here.
                            </p>

                            <small className="d-block mb-3">
                              (jpeg, png - Up to 15 mb)
                            </small>

                            <label className="btn btn-badge text-main-color">
                              Browse File
                              <input
                                hidden
                                type="file"
                                accept="image/jpeg,image/png"
                                onChange={(e) => {
                                  handleFileChange(
                                    e,
                                    "ExecutiveMember",
                                    "photo",
                                  );
                                }}
                              />
                            </label>
                          </>
                        )}
                      </div>
                      {executiveErrors.file && (
                        <p className="error-message">{executiveErrors.file}</p>
                      )}
                    </div>
                    <div className="col-12">
                      <label className="form-label">
                        Upload Executive Member Citizenship Photo Front and Back
                      </label>
                      <div className="row">
                        {(["citizenshipFront", "citizenshipBack"] as const).map(
                          (side) => {
                            const file = executiveCooperative[side];
                            const fileName =
                              executiveCooperative[`${side}Name`];
                            const url = executiveCooperative[`${side}Url`];
                            return (
                              <div className="col-md-6" key={side}>
                                <label className="form-label">
                                  Citizenship{" "}
                                  {side === "citizenshipFront"
                                    ? "Front"
                                    : "Back"}{" "}
                                  (JPEG, PNG){" "}
                                  <span className="primary-color">*</span>
                                </label>
                                <div
                                  className={`upload-box p-4 ${executiveErrors[side] ? "input-error" : ""} ${!file && !url ? "text-center" : ""}`}
                                >
                                  {file ? (
                                    renderFilePreview(
                                      file,
                                      fileName,
                                      (e: any) =>
                                        handleFileChange(
                                          e,
                                          "ExecutiveMember",
                                          side,
                                        ),
                                    )
                                  ) : url ? (
                                    renderExistingFilePreview(url, (e: any) =>
                                      handleFileChange(
                                        e,
                                        "ExecutiveMember",
                                        side,
                                      ),
                                    )
                                  ) : (
                                    <>
                                      <Upload />
                                      <p className="mb-1">
                                        Choose an image or drag and drop it
                                        here.
                                      </p>
                                      <small className="d-block mb-3">
                                        (jpeg, png - Up to 15 mb)
                                      </small>
                                      <label className="btn btn-badge text-main-color">
                                        Browse File
                                        <input
                                          hidden
                                          type="file"
                                          accept="image/jpeg,image/png"
                                          onChange={(e) =>
                                            handleFileChange(
                                              e,
                                              "ExecutiveMember",
                                              side,
                                            )
                                          }
                                        />
                                      </label>
                                    </>
                                  )}
                                </div>
                                {executiveErrors[side] && (
                                  <p className="error-message">
                                    {executiveErrors[side]}
                                  </p>
                                )}
                              </div>
                            );
                          },
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="d-flex mt-4 justify-content-end align-items-center gap-3">
                  {errorsExecutive && (
                    <p className="error-message">{errorsExecutive}</p>
                  )}
                  <button
                    className="btn btn-badge-primary px-3"
                    onClick={handleSaveExecutive}
                  >
                    {editExecutiveIndex !== null ? "Save Member" : "Add Member"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {committeeactiveTab === "committees" &&
        committeeTableCooperative &&
        committeeTableCooperative.length > 0 && (
          <CommiteeTable
            committeeData={committeeTableCooperative}
            setCommitteeCooperative={setCommitteeCooperative}
            setEditCommitteeIndex={setEditCommitteeIndex}
            editCommitteeIndex={editCommitteeIndex}
            setCommitteeTableCooperative={setCommitteeTableCooperative}
          />
        )}
      {committeeactiveTab === "executive" &&
        executiveTableCooperative &&
        executiveTableCooperative.length > 0 && (
          <ExecutiveTable
            executiveData={executiveTableCooperative}
            setExecutiveCooperative={setExecutiveCooperative}
            setEditExecutiveIndex={setEditExecutiveIndex}
            editExecutiveIndex={editExecutiveIndex}
            setExecutiveTableCooperative={setExecutiveTableCooperative}
          />
        )}
    </>
  );
};

export default Committee;
